import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { FeatureResource } from '@app/shared/resources/feature.resource';
import { NavigationService } from '@services/navigation/navigation.service';
import { map, shareReplay } from 'rxjs/operators';
import { FeatureWithVersionDTO } from '@app/shared/models/feature-firmware-version-dto.interface';

interface BuildingFeaturesState {
  disabledFeatures: string[];
  versionBoundFeatures: FeatureWithVersionDTO[];
}

function matchesFeatureName(target: string, candidate: string): boolean {
  if (!target || !candidate) {
    return false;
  }
  if (target === candidate || target.toLowerCase() === candidate.toLowerCase()) {
    return true;
  }
  const normalize = (s: string): string => s.toLowerCase().replace(/[-_]/g, '');
  return normalize(target) === normalize(candidate);
}

@Injectable({
  providedIn: 'root'
})
/*
  this service can be used either as a directive to remove elements
  or via the service directly.

  usage: isFeatureAvailable="Key of Feature in string"
 */
export class FeatureService {
  private readonly featureResource = inject(FeatureResource);
  private readonly navService = inject(NavigationService);

  private readonly buildingFeaturesCache = new Map<number, Observable<BuildingFeaturesState>>();

  isAvailable(key: string, nodeType?: string, picVersion?: number, nordicVersion?: number): Observable<boolean> {
    const buildingId = this.navService.buildingId;
    if (buildingId == null) {
      return this.featureResource.getFeature(key, nodeType, picVersion, nordicVersion);
    }
    return this.getBuildingFeatures(buildingId).pipe(
      map((state) =>
        this.evaluateFeature(key, state.disabledFeatures, state.versionBoundFeatures, nodeType, picVersion, nordicVersion)
      )
    );
  }

  isAvailableForBuildingId(
    key: string,
    buildingId: number,
    nodeType?: string,
    picVersion?: number,
    nordicVersion?: number
  ): Observable<boolean> {
    return this.getBuildingFeatures(buildingId).pipe(
      map((state) =>
        this.evaluateFeature(key, state.disabledFeatures, state.versionBoundFeatures, nodeType, picVersion, nordicVersion)
      )
    );
  }

  getVersionBoundFeatures(): Observable<FeatureWithVersionDTO[]> {
    return this.featureResource.getVersionBoundFeatures();
  }

  getVersionBoundFeaturesForBuilding(buildingId: number): Observable<FeatureWithVersionDTO[]> {
    return this.featureResource.getVersionBoundFeaturesForBuilding(buildingId);
  }

  clearCache(buildingId?: number): void {
    if (buildingId != null) {
      this.buildingFeaturesCache.delete(buildingId);
    } else {
      this.buildingFeaturesCache.clear();
    }
  }

  private getBuildingFeatures(buildingId: number): Observable<BuildingFeaturesState> {
    let cached = this.buildingFeaturesCache.get(buildingId);
    if (!cached) {
      cached = combineLatest([
        this.featureResource.getAllFeaturesForBuilding(buildingId),
        this.featureResource.getVersionBoundFeaturesForBuilding(buildingId)
      ]).pipe(
        map(([disabledFeatures, versionBoundFeatures]) => ({ disabledFeatures, versionBoundFeatures })),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.buildingFeaturesCache.set(buildingId, cached);
    }
    return cached;
  }

  private evaluateFeature(
    key: string,
    disabledFeatures: string[],
    versionBoundFeatures: FeatureWithVersionDTO[],
    nodeType?: string,
    picVersion?: number,
    nordicVersion?: number
  ): boolean {
    if (disabledFeatures && disabledFeatures.some((df) => matchesFeatureName(df, key))) {
      return false;
    }

    if (!versionBoundFeatures || versionBoundFeatures.length === 0) {
      return true;
    }

    const featureVersion = versionBoundFeatures.find((f) => matchesFeatureName(f.featureName, key));
    if (!featureVersion || !featureVersion.firmwareVersions || featureVersion.firmwareVersions.length === 0) {
      return true;
    }

    if (!nodeType) {
      return false;
    }

    const constraint = featureVersion.firmwareVersions.find(
      (v) => v.nodeType?.toUpperCase() === nodeType.toUpperCase()
    );
    if (!constraint) {
      return false;
    }

    const hasPicConstraint = constraint.picVersionMin != null || constraint.picVersionMax != null;
    if (hasPicConstraint) {
      if (picVersion == null) {
        return false;
      }
      if (constraint.picVersionMin != null && picVersion < constraint.picVersionMin) {
        return false;
      }
      if (constraint.picVersionMax != null && picVersion > constraint.picVersionMax) {
        return false;
      }
    }

    const hasNordicConstraint = constraint.nordicVersionMin != null || constraint.nordicVersionMax != null;
    if (hasNordicConstraint) {
      if (nordicVersion == null) {
        return false;
      }
      if (constraint.nordicVersionMin != null && nordicVersion < constraint.nordicVersionMin) {
        return false;
      }
      if (constraint.nordicVersionMax != null && nordicVersion > constraint.nordicVersionMax) {
        return false;
      }
    }

    return true;
  }
}

